(function ($) {
    'use strict';

    const RESPONSE_ERROR = {
        NO_RESULTS_FOUND: 'NO_RESULTS_FOUND',
        MISSING_PARAMETER: 'MISSING_PARAMETER',
        WRONG_FORMAT: 'WRONG_FORMAT'
    };

    const NL_POSTAL_CODE_REGEX = /^[0-9]{4,4}\s?[a-zA-Z]{2,2}$/; // valid for '5408xb' and '5408 XB'
    const BE_POSTAL_CODE_REGEX = /^\d{4}$/;
    const DE_POSTAL_CODE_REGEX = /^\d{5}$/;
    const STREETNUMBER_REGEX = /^[0-9]+$/;
    const timeout = 10000;
    const delay = 300;
    const pro6ppCache = {};
    let timer;

    /**
     * Handles the response from the Pro6PP Autocomplete API v2 and custom response with validation errors.
     * Prepares the result and passes it to the specified callback function.
     *
     * @param {jQuery} $instance - The instance that represents the scope of the current address form.
     * @param {Object} responseJSON - The object with response from the Pro6PP or with custom validation errors.
     */
    function handleResponse($instance, responseJSON) {
        const result = {};

        if (!responseJSON.error_id) {
            result.status = 'ok';
            result.street = responseJSON.street;
            result.city = responseJSON.settlement;
        } else {
            result.status = 'error';

            switch (responseJSON.error_id) {
                case RESPONSE_ERROR.WRONG_FORMAT:
                    delete responseJSON.error_id; // eslint-disable-line no-param-reassign
                    $.extend(result, responseJSON);
                    break;
                case RESPONSE_ERROR.NO_RESULTS_FOUND:
                    result.addressUnknown = $instance.find('input[name=unknownAddressErrorMessage]').val().trim();
                    break;
                case RESPONSE_ERROR.MISSING_PARAMETER:
                    responseJSON.errors.forEach(function (element) {
                        switch (element.parameter) {
                            case 'street':
                                result.address = true;
                                break;
                            case 'suite':
                                result.streetnumber = true;
                                break;
                            default:
                                if (element.parameter[0] === 'premise') {
                                    result.extension = true;
                                }
                                break;
                        }
                    });
                    break;
                default:
                    result.postalCode = true;
                    break;
            }
        }

        $instance.callback(result, $instance);
    }

    /**
     * Performs the actual call to retrieve the data from the Pro6PP Autocomplete API v2.
     * At first, it tries to get a response from the cache of already done requests.
     *
     * @param {jQuery} $instance - The instance that represents the scope of the current address form.
     * @param {string} url - The URL for making request.
     * @param {Object} params - The object with parameters to be sent as data.
     */
    function pro6ppCachedGet($instance, url, params) {
        const key = url + $.param(params);

        if (pro6ppCache[key]) {
            handleResponse($instance, pro6ppCache[key]);
        } else {
            $.spinner().start();
            $.ajax({
                dataType: 'json',
                timeout: timeout,
                url: url,
                data: params,
                success: function (responseJSON) {
                    pro6ppCache[key] = responseJSON;
                    handleResponse($instance, responseJSON);
                },
                error: function (response) {
                    handleResponse($instance, response.responseJSON);
                },
                complete: function () {
                    $.spinner().stop();
                }
            });
        }
    }

    /**
     * Verifies address fields, prepares request data, and makes the call to retrieve the data from the Pro6PP Autocomplete API v2.
     *
     * @param {jQuery} $instance - The instance that represents the scope of the current address form.
     */
    function autocomplete($instance) {
        clearTimeout(timer);

        const allowedCountries = ['NL', 'BE', 'DE'];
        const selectedCountry = $instance.country.val().trim();

        if (!allowedCountries.includes(selectedCountry)) {
            return;
        }

        const addressFields = [
            {
                key: 'postalCode',
                parameter: 'postalCode',
                value: $instance.postcode.val().trim(),
                required: ['NL', 'BE', 'DE'],
                regexp: {
                    NL: NL_POSTAL_CODE_REGEX,
                    BE: BE_POSTAL_CODE_REGEX,
                    DE: DE_POSTAL_CODE_REGEX
                },
                error: {
                    NL: $instance.find('input[name=invalidPostCodeErrorMessage_NL]').val().trim(),
                    BE: $instance.find('input[name=invalidPostCodeErrorMessage_BE]').val().trim(),
                    DE: $instance.find('input[name=invalidPostCodeErrorMessage_DE]').val().trim()
                }
            },
            {
                key: 'suite',
                parameter: 'streetNumber',
                value: $instance.streetnumber.val().trim(),
                required: ['NL', 'BE', 'DE'],
                regexp: STREETNUMBER_REGEX
            },
            {
                key: 'extension',
                parameter: 'premise',
                value: $instance.extension.val().trim()
            },
            {
                key: 'address',
                parameter: 'street',
                value: $instance.street.val().trim(),
                required: ['BE', 'DE']
            },
            {
                key: 'city',
                parameter: 'settlement',
                value: $instance.city.val().trim(),
                required: ['DE']
            }
        ];

        // checking for the format errors
        const responseJSON = addressFields.reduce(function (accumulator, field) {
            let isFieldValid = true;

            if (field.value && field.regexp && field.regexp instanceof RegExp) {
                isFieldValid = field.regexp.test(field.value);
            } else if (field.value && field.regexp && field.regexp instanceof Object) {
                isFieldValid = field.regexp[selectedCountry].test(field.value);
            }

            if (!isFieldValid) {
                accumulator[field.key] = field.error ? field.error[selectedCountry] : true; // eslint-disable-line no-param-reassign
            }

            return accumulator;
        }, { error_id: RESPONSE_ERROR.WRONG_FORMAT });

        if (Object.keys(responseJSON).length > 1) {
            handleResponse($instance, responseJSON);
            return;
        }

        // checking whether at least one required field is missing
        const isSomeRequiredFieldMissing = addressFields.some(function (field) {
            return field.required && field.required.includes(selectedCountry) && !field.value;
        });

        if (isSomeRequiredFieldMissing) {
            handleResponse($instance, {});
            return;
        }

        // preparing parameters for the request
        const params = addressFields.reduce(function (accumulator, field) {
            if (field.value) {
                accumulator[field.parameter] = field.value; // eslint-disable-line no-param-reassign
            }

            return accumulator;
        }, { authKey: window.pro6pp_auth_key });

        // handling the special case with an additional parameter
        if (params.premise) {
            params.streetNumberAndPremise = `${params.streetNumber} ${params.premise}`;
        }

        const url = 'https://api.pro6pp.nl/v2/autocomplete/' + selectedCountry.toLowerCase();

        timer = setTimeout(function () {
            pro6ppCachedGet($instance, url, params);
        }, delay);
    }

    /**
     * The main entry point to apply autocomplete functionality to the address form.
     *
     * @param {Function} callback - The function that will be called back after getting results from the autocomplete request. Required.
     * @param {Object} customSelectors - The object with custom selectors of the address form. Optional.
     */
    $.fn.applyAutocomplete = function (callback, customSelectors) {
        const defaultSelectors = {
            country: '.js-address-country',
            postcode: '.js-address-zipcode',
            streetnumber: '.js-address-suite',
            extension: '.js-address-suiteAddition',
            street: '.js-address-address1',
            city: '.js-address-city',
            submitButton: '.js-address-form-button'
        };

        const $instance = this;
        const selectors = $.extend({}, defaultSelectors, customSelectors);

        $instance.country = $instance.find(selectors.country);
        $instance.postcode = $instance.find(selectors.postcode);
        $instance.streetnumber = $instance.find(selectors.streetnumber);
        $instance.extension = $instance.find(selectors.extension);
        $instance.street = $instance.find(selectors.street);
        $instance.city = $instance.find(selectors.city);

        $instance.callback = typeof callback === 'function' ? callback : function () {};

        $instance.submitButton = $instance.find(selectors.submitButton);

        if (!$instance.submitButton.length) {
            $instance.parents().each(function (i, parent) {
                $instance.submitButton = $(parent).find(selectors.submitButton);

                return !$instance.submitButton.length;
            });
        }

        $instance.postcode.attr('autocomplete', 'off');
        $instance.postcode.focusout(function () {
            autocomplete($instance);
        });
        $instance.streetnumber.attr('autocomplete', 'off');
        $instance.streetnumber.focusout(function () {
            autocomplete($instance);
        });
        $instance.extension.attr('autocomplete', 'off');
        $instance.extension.focusout(function () {
            autocomplete($instance);
        });

        $instance.street.attr('autocomplete', 'off');
        $instance.street.focusout(function () {
            autocomplete($instance);
        });

        $instance.city.attr('autocomplete', 'off');
        $instance.city.focusout(function () {
            autocomplete($instance);
        });

        $instance.submitButton.on('click', function (event, skipHandling) {
            if (skipHandling) {
                return;
            }

            autocomplete($instance);

            event.preventDefault();
            event.stopImmediatePropagation();

            setTimeout(function () {
                if (!$instance.submitButton.is(':disabled') && $instance.submitButton.attr('disabled') !== 'disabled') {
                    $instance.submitButton.trigger('click', true);
                }
            }, 1000);
        });
    };
})(jQuery);
